/**
 * The trees the scope switcher offers (card 1.2), as plain data: one entry per
 * tree, in picker order. A key is the section id the sidebar always used
 * (`personal`, `device`, `team:<id>`) — or `personal/<scopeId>` for a user
 * workspace (card 5.2) — so the persisted expand sets, the reveal requests
 * (which match on that id) and the remembered choice all keep working. No
 * React here.
 *
 * Order and groups (card 5.2): Personal · My workspaces (the user's drag
 * order) · This device · Teams. `group` / `groupLabel` feed
 * CompactSelectPicker's group headings.
 *
 * `workspacesOn` (the `documents.scopes` flag): Personal then means "documents
 * in no workspace" (`scopeId: null`); off, it is the whole personal bucket as
 * before, so a workspace made on another device never hides a document here.
 */
import { PERSONAL_SCOPE, DEVICE_SCOPE } from './treeModel';

export const WORKSPACE_KEY_PREFIX = 'personal/';

/** The switcher key of a personal workspace (`null` = Personal). */
export const workspaceKey = (scopeId) => (scopeId ? `${WORKSPACE_KEY_PREFIX}${scopeId}` : 'personal');

export function scopeChoices({ t, showDevice = false, teams = [], workspaces = [], workspacesOn = false }) {
  const out = [
    {
      key: 'personal',
      kind: 'personal',
      group: 'personal',
      scope: workspacesOn ? { type: 'personal', scopeId: null } : PERSONAL_SCOPE,
      label: t('docSidebar.scope.personal'),
      emptyTitle: t('docSidebar.empty.title'),
      emptyHint: t('docSidebar.empty.personalHint'),
    },
  ];
  // Every entry is a docscope `{ id, name, icon, color }` (documentsScopes.js).
  for (const ws of workspacesOn ? workspaces : []) {
    out.push({
      key: workspaceKey(ws.id),
      kind: 'workspace',
      group: 'workspaces',
      groupLabel: t('docSidebar.workspaces.group'),
      scope: { type: 'personal', scopeId: ws.id },
      scopeId: ws.id,
      label: ws.name,
      icon: ws.icon,
      color: ws.color,
      emptyTitle: t('docSidebar.empty.title'),
      emptyHint: t('docSidebar.workspaces.emptyHint'),
    });
  }
  if (showDevice) {
    out.push({
      key: 'device',
      kind: 'device',
      group: 'device',
      scope: DEVICE_SCOPE,
      label: t('cloudLocal.deviceSection.title'),
      emptyTitle: t('cloudLocal.deviceSection.emptyTitle'),
      emptyHint: t('cloudLocal.deviceSection.emptyHint'),
    });
  }
  for (const team of teams) {
    if (team?.id == null) continue;
    out.push({
      key: `team:${team.id}`,
      kind: 'team',
      group: 'teams',
      groupLabel: t('docSidebar.workspaces.teams'),
      scope: { type: 'team', teamId: team.id },
      label: team.name || t('docSidebar.scope.team'),
      team,
      emptyTitle: t('docSidebar.empty.title'),
      emptyHint: t('docSidebar.empty.teamHint'),
    });
  }
  return out;
}

/** The choice for `key`, or Personal when that tree is not offered (any more). */
export function resolveChoice(choices, key) {
  return choices.find((c) => c.key === key) || choices[0];
}

/** One letter for the 20 px avatar chip. */
export function initialOf(name) {
  const ch = String(name || '').trim().charAt(0);
  return ch ? ch.toUpperCase() : '·';
}
